import React, { useEffect, lazy, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as HyperDX from "@hyperdx/browser";
import { Header, Footer } from "@containers";
import { UseNetworkStatus, get, loadPlausibleScript } from "@utils";
import {
  getBGimage,
  getClubsData,
  getHeaderData,
  getMenus,
  getSelectedPlayerData,
} from "@store";
import routes from "./route";
import Config from "./config";
import "./App.css";

const NotFound = lazy(() => import("@components/notFound"));

function App() {
  const dispatch = useDispatch();
  const { isOnline } = UseNetworkStatus();
  const { i18n } = useTranslation("common");
  const bgImage = useSelector((state) => state.setting.bgImage);
  const isImg = bgImage?.data?.data?.bgImage?.url;
  const data = useSelector((state) => state.header.headerData);
  const headerMenu = useSelector((state) => state.menu.menus);
  const clubData = useSelector((state) => state.clubs.clubsData);

  const [isServerError, setIsServerError] = useState(false);
  const [isServerLoading, setIsServerLoading] = useState(false);

  useEffect(() => {
    const hostname = window.location.hostname;

    const parts = hostname.split(".");
    const service =
      parts.length >= 4 ? parts.slice(0, parts.length - 2).join(".") : hostname;

    let environment = "production";

    if (hostname.startsWith("demo-") || hostname.startsWith("localhost")) {
      environment = "development";
    } else if (hostname.startsWith("staging-")) {
      environment = "staging";
    } else if (hostname.startsWith("bookings.")) {
      environment = "production";
    }

    HyperDX.init({
      apiKey: Config.HYPERDX_API_KEY || "",
      service: service || "K-club",
      environment: environment,
      maskAllInputs: false,
      maskAllText: false,
      disableIntercom: true,
      disableReplay: false,
      enableAutoNetworkInstrumentation: true,
      enableNetworkCapture: true,
      advancedNetworkCapture: true,
      beforeSend(event) {
        // Check for routing-related errors
        const isRoutingError =
          event.error &&
          (event.error.message.includes("Cannot match any routes") ||
            event.error.message.includes("No routes matched location") ||
            event.error.message.includes("404") ||
            event.error.message.includes("chunk") ||
            event.error.message.includes("Loading chunk") ||
            event.error.message.includes("thehotelsnetwork.com") ||
            event.error.message.includes("bat.bing.com"));

        if (isRoutingError) {
          console.warn(
            "*** Routing error intercepted by HyperDX:",
            event.error.message
          );
          return false;
        }
        return true;
      },
      // ignoreUrls: [/thehotelsnetwork\.com/i, /bat\.bing\.com/i],
    });

    const script = document.createElement("script");
    script.defer = true;
    script.dataset.domain = hostname;
    script.dataset.api = "https://t.g365.golf/api/event";
    script.src =
      "https://t.g365.golf/js/script.hash.outbound-links.pageview-props.revenue.tagged-events.js";
    document.head.appendChild(script);

    // Initialize plausible global function
    window.plausible =
      window.plausible ||
      function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };
  }, []);

  useEffect(() => {
    dispatch(getBGimage());
    dispatch(getHeaderData());
    dispatch(getMenus());
    dispatch(getClubsData());

    const selectLanguage = get("Language");
    if (selectLanguage !== null) {
      i18n.changeLanguage(selectLanguage);
    }
  }, [i18n, dispatch]);

  useEffect(() => {
    if (
      !bgImage?.data?.data?.id &&
      !clubData?.data?.id &&
      !headerMenu?.length &&
      !data?.id
    ) {
      setIsServerLoading(true);
    } else {
      setIsServerError(false);
      setIsServerLoading(false);
    }
  }, [bgImage, headerMenu, data, clubData]);

  useEffect(() => {
    let interval = null;

    if (isServerLoading) {
      interval = setInterval(() => {
        setIsServerError(true);
      }, 5000);
      return () => clearInterval(interval);
    } else {
      clearInterval(interval);
      setIsServerError(false);
    }
  }, [isServerLoading]);

  useEffect(() => {
    const checkCartExpiration = () => {
      const lastUpdated = parseInt(localStorage.getItem("CartTimer"));
      const now = new Date().getTime();
      if (lastUpdated && now - lastUpdated > 30 * 60 * 1000) {
        localStorage.removeItem("LocalCartData");
        localStorage.removeItem("CartTimer");
        dispatch(getSelectedPlayerData({ save: false }));
      }
    };
    checkCartExpiration();
    const interval = setInterval(checkCartExpiration, 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  return (
    <div>
      {isServerError ? (
        <div
          style={{
            width: "100%",
            height: "200px",
            display: "flex",
            margin: "auto",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-around",
            backgroundColor: "white",
          }}
        >
          <div>
            <h1
              style={{
                marginTop: "50px",
                color: "gray",
                fontWeight: "bold",
                fontSize: "20px",
                textAlign: "center",
              }}
            >
              Welcome to The K Club{" "}
            </h1>
          </div>
          <div
            style={{
              marginTop: "50px",
              color: "#9C8053",
              fontWeight: "bold",
              fontSize: "30px",
            }}
          >
            <h1 className="animate-pulse text-center">
              Server under maintenance{" "}
            </h1>
          </div>
        </div>
      ) : isServerLoading ? (
        <div className="w-full h-[300px] flex justify-center items-center">
          <svg className="w-16 h-16 bg-transparent border-4 animate-spin rounded-full border-t-[white] border-[#9C8053]" />
        </div>
      ) : (
        <div
          style={{
            backgroundImage: `url(${isImg})`,
          }}
          className="background-Img"
        >
          <div
            style={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "start",
              margin: "auto",
              width: "full",
              height: "full",
              backgroundColor: "rgba(18,16,55,0.50)",
            }}
          >
            {isOnline ? (
              <div
                style={{
                  width: "100%",
                  maxWidth: "1600px",
                  height: "auto",
                  minHeight: "100vh",
                }}
              >
                <Routes>
                  {routes.map((item, index) => (
                    <Route
                      key={"" + index}
                      exact={item.exact}
                      isPublic={item.isPublic}
                      path={item.path}
                      name={item.name}
                      element={
                        <>
                          <Header />
                          <div
                            style={{
                              minHeight: window.innerHeight * 0.67,
                              maxWidth: "1500px",
                              width: "100%",
                              margin: "auto",
                            }}
                          >
                            {item.element}
                          </div>
                          <Footer />
                        </>
                      }
                    />
                  ))}
                  <Route
                    exact={true}
                    isPublic={true}
                    path={"*"}
                    name={"NotFound"}
                    element={<NotFound />}
                  />
                </Routes>
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "200px",
                  display: "flex",
                  margin: "auto",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "space-around",
                  backgroundColor: "white",
                }}
              >
                <div>
                  <h1
                    style={{
                      marginTop: "50px",
                      color: "gray",
                      fontWeight: "bold",
                      fontSize: "20px",
                    }}
                  >
                    Welcome to The K Club{" "}
                  </h1>
                </div>
                <div
                  style={{
                    marginTop: "50px",
                    color: "green",
                    fontWeight: "bold",
                    fontSize: "30px",
                  }}
                >
                  <h1 className="animate-pulse ">Your are Offline </h1>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
